import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { fn, hash } from "@ember/helper";
import { service } from "@ember/service";
import { eq, or } from "truth-helpers";
import PluginOutlet from "discourse/components/plugin-outlet";
import TopicListHeader from "discourse/components/topic-list/topic-list-header";
import TopicListItem from "discourse/components/topic-list/topic-list-item";
import concatClass from "discourse/helpers/concat-class";
import i18n from "discourse-common/helpers/i18n";
import { setComponentTemplate } from "@ember/component";
import { createTemplateFactory } from "@ember/template-factory";
export default class TopicList extends Component {
  static #_ = (() => dt7948.g(this.prototype, "currentUser", [service]))();
  #currentUser = (() => (dt7948.i(this, "currentUser"), void 0))();
  static #_2 = (() => dt7948.g(this.prototype, "router", [service]))();
  #router = (() => (dt7948.i(this, "router"), void 0))();
  static #_3 = (() => dt7948.g(this.prototype, "siteSettings", [service]))();
  #siteSettings = (() => (dt7948.i(this, "siteSettings"), void 0))();
  static #_4 = (() => dt7948.g(this.prototype, "lastCheckedElementId", [tracked]))();
  #lastCheckedElementId = (() => (dt7948.i(this, "lastCheckedElementId"), void 0))();
  get selected() {
    return this.args.bulkSelectHelper?.selected;
  }
  get bulkSelectEnabled() {
    return this.args.bulkSelectHelper?.bulkSelectEnabled;
  }
  get canDoBulkActions() {
    return this.currentUser?.canManageTopic && this.selected?.length;
  }
  get toggleInTitle() {
    return !this.bulkSelectEnabled && this.args.canBulkSelect;
  }
  get experimentalTopicBulkActionsEnabled() {
    return this.currentUser?.use_experimental_topic_bulk_actions;
  }
  get sortable() {
    return !!this.args.changeSort;
  }
  get showLikes() {
    return this.args.order === "likes";
  }
  get showOpLikes() {
    return this.args.order === "op_likes";
  }
  get lastVisitedTopic() {
    const {
      topics: topics1,
      order: order1,
      ascending: ascending1,
      top: top1,
      hot: hot1
    } = this.args;
    if (!this.args.highlightLastVisited || top1 || hot1 || ascending1 || !topics1 || topics1.length === 1 || order1 && order1 !== "activity" || !this.currentUser?.get("previous_visit_at")) {
      return;
    }
    // work backwards
    // this is more efficient cause we keep appending to list
    const start1 = topics1.findIndex(topic1 => !topic1.get("pinned"));
    let lastVisitedTopic1, topic1;
    for (let i1 = topics1.length - 1; i1 >= start1; i1--) {
      if (topics1[i1].get("bumpedAt") > this.currentUser.get("previousVisitAt")) {
        lastVisitedTopic1 = topics1[i1];
        break;
      }
      topic1 = topics1[i1];
    }
    if (!lastVisitedTopic1 || !topic1) {
      return;
    }
    // end of list that was scanned
    if (topic1.get("bumpedAt") > this.currentUser.get("previousVisitAt")) {
      return;
    }
    return lastVisitedTopic1;
  }
  get showTopicPostBadges() {
    return this.args.showTopicPostBadges ?? true;
  }
  static #_5 = (() => setComponentTemplate(createTemplateFactory(
  /*
    
      {{! template-lint-disable table-groups }}
      <table
        class={{concatClass
          "topic-list"
          (if this.bulkSelectEnabled "sticky-header")
        }}
      >
        <thead class="topic-list-header">
          <TopicListHeader
            @canBulkSelect={{@canBulkSelect}}
            @toggleInTitle={{this.toggleInTitle}}
            @category={{@category}}
            @hideCategory={{@hideCategory}}
            @showPosters={{@showPosters}}
            @showLikes={{this.showLikes}}
            @showOpLikes={{this.showOpLikes}}
            @order={{@order}}
            @changeSort={{@changeSort}}
            @ascending={{@ascending}}
            @sortable={{this.sortable}}
            @listTitle={{or @listTitle "topic.title"}}
            @bulkSelectEnabled={{this.bulkSelectEnabled}}
            @bulkSelectHelper={{@bulkSelectHelper}}
            @experimentalTopicBulkActionsEnabled={{this.experimentalTopicBulkActionsEnabled}}
            @canDoBulkActions={{this.canDoBulkActions}}
            @showTopicsAndRepliesToggle={{@showTopicsAndRepliesToggle}}
            @newListSubset={{@newListSubset}}
            @newRepliesCount={{@newRepliesCount}}
            @newTopicsCount={{@newTopicsCount}}
            @changeNewListSubset={{@changeNewListSubset}}
          />
        </thead>
  
        <PluginOutlet
          @name="before-topic-list-body"
          @outletArgs={{hash
            topics=@topics
            selected=this.selected
            bulkSelectEnabled=this.bulkSelectEnabled
            lastVisitedTopic=this.lastVisitedTopic
            discoveryList=@discoveryList
            hideCategory=@hideCategory
          }}
        />
  
        <tbody class="topic-list-body">
          {{#each @topics as |topic index|}}
            <TopicListItem
              @topic={{topic}}
              @bulkSelectEnabled={{this.bulkSelectEnabled}}
              @showTopicPostBadges={{this.showTopicPostBadges}}
              @hideCategory={{@hideCategory}}
              @showPosters={{@showPosters}}
              @showLikes={{this.showLikes}}
              @showOpLikes={{this.showOpLikes}}
              @expandGloballyPinned={{@expandGloballyPinned}}
              @expandAllPinned={{@expandAllPinned}}
              @lastVisitedTopic={{this.lastVisitedTopic}}
              @selected={{this.selected}}
              @lastCheckedElementId={{this.lastCheckedElementId}}
              @updateLastCheckedElementId={{fn (mut this.lastCheckedElementId)}}
              @tagsForUser={{@tagsForUser}}
              @focusLastVisitedTopic={{@focusLastVisitedTopic}}
              @index={{index}}
            />
  
            {{#if (eq topic this.lastVisitedTopic)}}
              <tr class="topic-list-item-separator">
                <td class="topic-list-data" colspan="6">
                  <span>
                    {{i18n "topics.new_messages_marker"}}
                  </span>
                </td>
              </tr>
            {{/if}}
  
            <PluginOutlet
              @name="after-topic-list-item"
              @outletArgs={{hash topic=topic index=index}}
              @connectorTagName="tr"
            />
          {{/each}}
        </tbody>
  
        <PluginOutlet
          @name="after-topic-list-body"
          @outletArgs={{hash
            topics=@topics
            selected=this.selected
            bulkSelectEnabled=this.bulkSelectEnabled
            lastVisitedTopic=this.lastVisitedTopic
            discoveryList=@discoveryList
            hideCategory=@hideCategory
          }}
        />
      </table>
    
  */
  {
    "id": "w54Wmm8g",
    "block": "[[[1,\"\\n\"],[1,\"    \"],[10,\"table\"],[15,0,[28,[32,0],[\"topic-list\",[52,[30,0,[\"bulkSelectEnabled\"]],\"sticky-header\"]],null]],[12],[1,\"\\n      \"],[10,\"thead\"],[14,0,\"topic-list-header\"],[12],[1,\"\\n        \"],[8,[32,1],null,[[\"@canBulkSelect\",\"@toggleInTitle\",\"@category\",\"@hideCategory\",\"@showPosters\",\"@showLikes\",\"@showOpLikes\",\"@order\",\"@changeSort\",\"@ascending\",\"@sortable\",\"@listTitle\",\"@bulkSelectEnabled\",\"@bulkSelectHelper\",\"@experimentalTopicBulkActionsEnabled\",\"@canDoBulkActions\",\"@showTopicsAndRepliesToggle\",\"@newListSubset\",\"@newRepliesCount\",\"@newTopicsCount\",\"@changeNewListSubset\"],[[30,1],[30,0,[\"toggleInTitle\"]],[30,2],[30,3],[30,4],[30,0,[\"showLikes\"]],[30,0,[\"showOpLikes\"]],[30,5],[30,6],[30,7],[30,0,[\"sortable\"]],[28,[32,2],[[30,8],\"topic.title\"],null],[30,0,[\"bulkSelectEnabled\"]],[30,9],[30,0,[\"experimentalTopicBulkActionsEnabled\"]],[30,0,[\"canDoBulkActions\"]],[30,10],[30,11],[30,12],[30,13],[30,14]]],null],[1,\"\\n      \"],[13],[1,\"\\n\\n      \"],[8,[32,3],null,[[\"@name\",\"@outletArgs\"],[\"before-topic-list-body\",[28,[32,4],null,[[\"topics\",\"selected\",\"bulkSelectEnabled\",\"lastVisitedTopic\",\"discoveryList\",\"hideCategory\"],[[30,15],[30,0,[\"selected\"]],[30,0,[\"bulkSelectEnabled\"]],[30,0,[\"lastVisitedTopic\"]],[30,16],[30,3]]]]]],null],[1,\"\\n\\n      \"],[10,\"tbody\"],[14,0,\"topic-list-body\"],[12],[1,\"\\n\"],[42,[28,[31,2],[[28,[31,2],[[30,15]],null]],null],null,[[[1,\"          \"],[8,[32,5],null,[[\"@topic\",\"@bulkSelectEnabled\",\"@showTopicPostBadges\",\"@hideCategory\",\"@showPosters\",\"@showLikes\",\"@showOpLikes\",\"@expandGloballyPinned\",\"@expandAllPinned\",\"@lastVisitedTopic\",\"@selected\",\"@lastCheckedElementId\",\"@updateLastCheckedElementId\",\"@tagsForUser\",\"@focusLastVisitedTopic\",\"@index\"],[[30,17],[30,0,[\"bulkSelectEnabled\"]],[30,0,[\"showTopicPostBadges\"]],[30,3],[30,4],[30,0,[\"showLikes\"]],[30,0,[\"showOpLikes\"]],[30,19],[30,20],[30,0,[\"lastVisitedTopic\"]],[30,0,[\"selected\"]],[30,0,[\"lastCheckedElementId\"]],[28,[32,6],[[28,[31,3],[[30,0,[\"lastCheckedElementId\"]]],null]],null],[30,21],[30,22],[30,18]]],null],[1,\"\\n\\n\"],[41,[28,[32,7],[[30,17],[30,0,[\"lastVisitedTopic\"]]],null],[[[1,\"            \"],[10,\"tr\"],[14,0,\"topic-list-item-separator\"],[12],[1,\"\\n              \"],[10,\"td\"],[14,0,\"topic-list-data\"],[14,\"colspan\",\"6\"],[12],[1,\"\\n                \"],[10,1],[12],[1,\"\\n                  \"],[1,[28,[32,8],[\"topics.new_messages_marker\"],null]],[1,\"\\n                \"],[13],[1,\"\\n              \"],[13],[1,\"\\n            \"],[13],[1,\"\\n\"]],[]],null],[1,\"\\n          \"],[8,[32,3],null,[[\"@name\",\"@outletArgs\",\"@connectorTagName\"],[\"after-topic-list-item\",[28,[32,4],null,[[\"topic\",\"index\"],[[30,17],[30,18]]]],\"tr\"]],null],[1,\"\\n\"]],[17,18]],null],[1,\"      \"],[13],[1,\"\\n\\n      \"],[8,[32,3],null,[[\"@name\",\"@outletArgs\"],[\"after-topic-list-body\",[28,[32,4],null,[[\"topics\",\"selected\",\"bulkSelectEnabled\",\"lastVisitedTopic\",\"discoveryList\",\"hideCategory\"],[[30,15],[30,0,[\"selected\"]],[30,0,[\"bulkSelectEnabled\"]],[30,0,[\"lastVisitedTopic\"]],[30,16],[30,3]]]]]],null],[1,\"\\n    \"],[13],[1,\"\\n  \"]],[\"@canBulkSelect\",\"@category\",\"@hideCategory\",\"@showPosters\",\"@order\",\"@changeSort\",\"@ascending\",\"@listTitle\",\"@bulkSelectHelper\",\"@showTopicsAndRepliesToggle\",\"@newListSubset\",\"@newRepliesCount\",\"@newTopicsCount\",\"@changeNewListSubset\",\"@topics\",\"@discoveryList\",\"topic\",\"index\",\"@expandGloballyPinned\",\"@expandAllPinned\",\"@tagsForUser\",\"@focusLastVisitedTopic\"],false,[\"if\",\"each\",\"-track-array\",\"mut\"]]",
    "moduleName": "/var/www/discourse/app/assets/javascripts/discourse/node_modules/.embroider/rewritten-app/components/topic-list/list.js",
    "scope": () => [concatClass, TopicListHeader, or, PluginOutlet, hash, TopicListItem, fn, eq, i18n],
    "isStrictMode": true
  }), this))();
}